import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import type { UseGetStockingLineageResult } from '@/domain/stockingCycles';
import { formatDateShortLocalized } from '@/utils/date/localized';

import styles from './StockingAssignment.module.scss';

interface LineageGroupsProps {
    lineage: UseGetStockingLineageResult;
    /** A stocking's display name, for the groups already in one. */
    cycleName: (stockingCycleId: number) => string;
}

/** The groups that move with the one the dialog is for, or why they can't be listed yet. */
export default function LineageGroups({ lineage, cycleName }: LineageGroupsProps) {
    const { t, i18n } = useTranslation('fishGroups');
    const headingId = useId();

    if (lineage.error) {
        return (
            <Alert
                tone="danger"
                size="small"
                action={
                    <Button
                        variant="tertiary"
                        size="small"
                        disabled={lineage.isFetching}
                        onClick={lineage.refetch}>
                        {t('stockingAssignment.lineage.retry')}
                    </Button>
                }>
                {t('stockingAssignment.lineage.failed')}
            </Alert>
        );
    }
    if (!lineage.data) {
        return <p className={styles.status}>{t('stockingAssignment.lineage.loading')}</p>;
    }

    // A refused lineage lists only the group asked for, which would read as "moves alone".
    if (lineage.data.blocker) return null;
    const { groups } = lineage.data;
    return (
        <section className={styles.section} aria-labelledby={headingId}>
            <Overline as="h3" id={headingId} className={styles.sectionLabel}>
                {t('stockingAssignment.lineage.heading', { count: groups.length })}
            </Overline>
            <p className={styles.hint}>{t('stockingAssignment.lineage.description')}</p>
            <Card variant="subtle" padding="none" radius="sm">
                <ul className={styles.groups}>
                    {groups.map((group) => (
                        <li key={group.fishGroupId} className={styles.group}>
                            <span className={styles.groupName}>
                                {group.name}
                                {group.requested && (
                                    <Badge tone="info">
                                        {t('stockingAssignment.lineage.thisGroup')}
                                    </Badge>
                                )}
                            </span>
                            <span className={styles.groupMeta}>
                                {[
                                    t(
                                        group.isLive
                                            ? 'stockingAssignment.lineage.live'
                                            : 'stockingAssignment.lineage.ended'
                                    ),
                                    group.startedAt &&
                                        t('stockingAssignment.lineage.startedOn', {
                                            date: formatDateShortLocalized(
                                                group.startedAt,
                                                i18n.language
                                            ),
                                        }),
                                    group.stockingCycleId !== null &&
                                        t('stockingAssignment.lineage.inStocking', {
                                            name: cycleName(group.stockingCycleId),
                                        }),
                                ]
                                    .filter(Boolean)
                                    .join(t('board.banner.separator'))}
                            </span>
                        </li>
                    ))}
                </ul>
            </Card>
        </section>
    );
}
